import { useMutation, useQueryClient } from '@tanstack/react-query';

import { acceptSuggestion, dismissSuggestion, regenerateDailyPlan, generateNextAction } from '../../api/crmApi';
import { activitiesKeys } from '../activities/keys';
import { dealsKeys } from '../deals/keys';

import { insightsKeys, insightsMutationKeys } from './keys';

export function useAcceptSuggestion() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: insightsMutationKeys.accept,
		mutationFn: acceptSuggestion,
		// Optimistic update
		onMutate: async (suggestionId) => {
			await qc.cancelQueries({ queryKey: insightsKeys.dailyPlan() });

			// Snapshot current state for rollback
			const previousDailyPlan = qc.getQueryData(insightsKeys.dailyPlan());

			// Optimistically remove the accepted suggestion from the daily plan
			qc.setQueryData(insightsKeys.dailyPlan(), (old) => {
				if (!old) return old;
				return { ...old, items: (old.items ?? []).filter((s) => s.id !== suggestionId) };
			});

			return { previousDailyPlan };
		},
		// Rollback on server error
		onError: (err, vars, context) => {
			if (context?.previousDailyPlan) {
				qc.setQueryData(insightsKeys.dailyPlan(), context.previousDailyPlan);
			}
		},
		onSettled: (data) => {
			qc.invalidateQueries({ queryKey: insightsKeys.all });
			const activity = data?.activity;
			if (activity?.dealId) {
				qc.invalidateQueries({ queryKey: activitiesKeys.byDeal(activity.dealId) });
				qc.invalidateQueries({ queryKey: dealsKeys.detail(activity.dealId) });
				qc.invalidateQueries({ queryKey: ['crm', 'nextAction', activity.dealId] });
			}
			if (activity?.crmCompanyId) {
				qc.invalidateQueries({ queryKey: activitiesKeys.byCompany(activity.crmCompanyId) });
			}
		},
	});
}

export function useDismissSuggestion() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: insightsMutationKeys.dismiss,
		mutationFn: dismissSuggestion,
		// Optimistic update
		onMutate: async (suggestionId) => {
			await qc.cancelQueries({ queryKey: insightsKeys.dailyPlan() });

			// Snapshot current state for rollback
			const previousDailyPlan = qc.getQueryData(insightsKeys.dailyPlan());

			// Optimistically remove the dismissed suggestion from the daily plan
			qc.setQueryData(insightsKeys.dailyPlan(), (old) => {
				if (!old) return old;
				return { ...old, items: (old.items ?? []).filter((s) => s.id !== suggestionId) };
			});

			return { previousDailyPlan };
		},
		// Rollback on server error
		onError: (err, vars, context) => {
			if (context?.previousDailyPlan) {
				qc.setQueryData(insightsKeys.dailyPlan(), context.previousDailyPlan);
			}
		},
		// Only invalidate insights (dismiss doesn't create activities/deals)
		onSettled: () => qc.invalidateQueries({ queryKey: insightsKeys.all }),
	});
}

export function useRegenerateDailyPlan() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: insightsMutationKeys.regenerateDaily,
		mutationFn: regenerateDailyPlan,
		onSuccess: () => qc.invalidateQueries({ queryKey: insightsKeys.dailyPlan() }),
	});
}

export function useGenerateNextAction() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: insightsMutationKeys.nextAction,
		mutationFn: generateNextAction,
		onSuccess: () => qc.invalidateQueries({ queryKey: insightsKeys.all }),
	});
}
